import css from './wordPane.module.scss';

import cx from 'classnames';
import { useContext, useState, useEffect, useCallback, memo } from 'react';

import { NSFW_DISABLED_LS_KEY, NSFW_TAG } from 'com/NSFW/storage';
import { apiFinishWord } from 'core/api';
import { useAudioBlob } from 'core/helpers/audio';
import { isCardFinished, isWordReadyToExercise } from 'core/helpers/exercise';
import {
  TRepetitionAction,
  TRepetitionActionType,
} from 'core/helpers/repetition';
import useLocalStorageState from 'core/hooks/browser/useLocalStorageState';
import { flashCtx } from 'core/ui/Frame';
import { useMessageModal } from 'core/ui/MessageModal';

import BackPane from './BackPane';
import DefinitionPane from './DefinitionPane';
import ResultPane from './ResultPane';
import ValuePane from './ValuePane';
import { TWordCardSide } from './shared';

type TProps = {
  deck: TDeck;
  word: TWord;
  goNext: () => void;
  isLast: boolean;
  disabled: boolean;
  exercise: keyof Pick<TExerciseEnum, 'wordCards' | 'wordCardsReverse'>;
  setUndo: (undo: TUndo) => void;
};

const WordPane: React.FC<TProps> = ({
  deck,
  word,
  goNext,
  isLast,
  disabled,
  exercise,
  setUndo,
}) => {
  const { showError } = useMessageModal();

  const [side, setSide] = useState(TWordCardSide.front);
  const [isAnimating, setIsAnimating] = useState(false);
  const finished = isCardFinished(side);
  const isBlurred = useNSFW(word);
  const isReadyToExercise = isWordReadyToExercise(word, exercise);

  const flash = orDie(useContext(flashCtx));

  useEffect(() => {
    setSide(TWordCardSide.front);
  }, [word.uid]);

  const finishWord = useCallback(
    async (action: TRepetitionAction) => {
      goNext();
      if (action.type === TRepetitionActionType.reset) flash('bad');
      else if (action.type === TRepetitionActionType.shift) flash('good');

      await apiFinishWord(word.uid, exercise, action)
        .then(setUndo)
        .catch(showError);
    },
    [goNext, word.uid]
  );

  useAudioBlob(word.uid); // Preload.

  return (
    <div className={cx(css.wordPane, isBlurred && css.blurred)}>
      {exercise === TExercise.wordCards && (
        <DefinitionPane deck={deck} word={word} finished={finished} />
      )}
      {exercise === TExercise.wordCardsReverse && (
        <ValuePane
          deck={deck}
          disabled={disabled}
          word={word}
          finished={finished}
        />
      )}
      <BackPane
        className={css.backPane}
        deck={deck}
        word={word}
        setSide={setSide}
        side={side}
        setIsAnimating={setIsAnimating}
        exercise={exercise}
        disabled={disabled || isAnimating}
      />
      <ResultPane
        className={css.resultPane}
        word={word}
        exercise={exercise}
        side={side}
        goNext={goNext}
        isLast={isLast}
        finishWord={finishWord}
        disabled={disabled || isAnimating || !isReadyToExercise}
      />
    </div>
  );
};

export default memo(WordPane);

function useNSFW(word: TWord): boolean {
  const [isNSFWDisabled] = useLocalStorageState(NSFW_DISABLED_LS_KEY, false);
  return (
    isNSFWDisabled &&
    ((word.valueNote || '') + (word.note || ''))
      .toUpperCase()
      .includes(NSFW_TAG)
  );
}
